/**
 * Keep browsing (sections/browse-tabs.liquid).
 *
 * One band with coloured tab buttons in place of three separate rows: "From this set", "Same character" and "Recently
 * viewed". Each tab is found with the same rules and the same shared requests as the rows it replaces
 * (assets/sold-out-alternatives.js: the group request for the set and the character, the card request plus one id request
 * for recently viewed), so the page makes no extra requests for it.
 *
 * A tab is shown only when it has at least one product; the first tab with products is selected. When no tab has
 * products the whole band stays hidden, so it never leaves an empty gap on the page. Nothing is requested while the page
 * loads: the band waits until it is within about 600px of the viewport.
 */

const DEFAULT_SOURCE = 'sold-out-alternatives';
const DEFAULT_MAX = 10;
const HISTORY_KEYS = ['viewedProductsHistory', 'viewedProducts'];

class BrowseTabs extends HTMLElement {
  /** @type {any} */
  #helpers = null;
  /** @type {(() => void) | null} */
  #cancel = null;

  connectedCallback() {
    if (this.hasAttribute('data-started')) return;
    this.setAttribute('data-started', '');
    this.#wire();

    const url = this.dataset.helpers;
    if (!url) return;
    import(url)
      .then((helpers) => {
        this.#helpers = helpers;
        this.#cancel = helpers.whenNear(this, () => {
          this.#run().catch(() => {});
        });
      })
      .catch(() => {});
  }

  disconnectedCallback() {
    this.#cancel?.();
  }

  /** Tab clicks, arrow keys and the Clear button. */
  #wire() {
    for (const tab of this.#tabs()) {
      tab.addEventListener('click', () => this.#select(tab.dataset.btTab || '', false));
    }

    this.querySelector('[role="tablist"]')?.addEventListener('keydown', (event) => {
      const ke = /** @type {KeyboardEvent} */ (event);
      const visible = this.#tabs().filter((tab) => !tab.hidden);
      const index = visible.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      if (index < 0) return;
      let next = -1;
      if (ke.key === 'ArrowRight') next = (index + 1) % visible.length;
      else if (ke.key === 'ArrowLeft') next = (index - 1 + visible.length) % visible.length;
      else if (ke.key === 'Home') next = 0;
      else if (ke.key === 'End') next = visible.length - 1;
      if (next < 0) return;
      ke.preventDefault();
      this.#select(visible[next]?.dataset.btTab || '', true);
    });

    this.querySelector('[data-bt-clear]')?.addEventListener('click', () => this.#clearHistory());
  }

  /** @returns {HTMLElement[]} */
  #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-bt-tab]')]);
  }

  /** @param {string} key */
  #panel(key) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-bt-panel="${key}"]`));
  }

  async #run() {
    const h = this.#helpers;
    const ds = this.dataset;
    const me = h.meFrom(ds);
    const source = ds.source || DEFAULT_SOURCE;
    const max = Math.min(20, Math.max(1, parseInt(ds.max || String(DEFAULT_MAX), 10) || DEFAULT_MAX));
    const wanted = (ds.tabs || '').split(',').filter(Boolean);

    /** @type {Record<string, Promise<any[]>>} */
    const jobs = {
      set: wanted.includes('set') ? this.#set(h, source, me, max) : Promise.resolve([]),
      character: wanted.includes('character') ? this.#character(h, source, me, max) : Promise.resolve([]),
      viewed: wanted.includes('viewed') ? this.#viewed(h, source, me, max) : Promise.resolve([]),
    };
    const keys = Object.keys(jobs);
    const settled = await Promise.allSettled(keys.map((key) => jobs[key]));

    /** @type {Record<string, any[]>} */
    const found = {};
    keys.forEach((key, i) => {
      const result = settled[i];
      found[key] = result && result.status === 'fulfilled' ? result.value.slice(0, max) : [];
    });
    this.#render(found, me);
  }

  /** Other in-stock products of the same set and the same kind (same rules as "More from this set"). */
  async #set(/** @type {any} */ h, /** @type {string} */ source, /** @type {any} */ me, /** @type {number} */ need) {
    if (!h.usableSet(me.vendor) || (me.kind !== 'single' && me.kind !== 'sealed')) return [];
    const kept = await h.groupItems(source, me, {
      keep: (/** @type {any} */ item) =>
        String(item.id) !== me.id &&
        item.a === 1 &&
        item.v === me.vendor &&
        item.k === me.kind &&
        (me.game === 'other' || item.g === me.game),
      own: `vendor:"${h.quoted(me.vendor)}"`,
      need,
    });
    return h.rank(me, h.rotate(kept, h.seedOf(me.id)));
  }

  /** Other in-stock cards of the same character, not printings of this card (same rules as "Same character"). */
  async #character(/** @type {any} */ h, /** @type {string} */ source, /** @type {any} */ me, /** @type {number} */ need) {
    if (!me.base || (me.game !== 'pokemon' && me.game !== 'onepiece') || (me.kind !== 'single' && me.kind !== 'graded')) {
      return [];
    }
    const needle = ` ${h.normalize(me.base)} `;
    const kept = await h.groupItems(source, me, {
      keep: (/** @type {any} */ item) =>
        String(item.id) !== me.id &&
        item.a === 1 &&
        item.k === 'single' &&
        item.g === me.game &&
        ` ${h.normalize(h.cardName(item.t))} `.includes(needle) &&
        !h.sameCard(me, item),
      own: `title:"${h.quoted(me.base)}"`,
      need,
    });
    return h.rank(me, h.rotate(kept, h.seedOf(me.id)));
  }

  /** The products this shopper viewed before, newest first, without this one (same list as "Recently viewed"). */
  async #viewed(/** @type {any} */ h, /** @type {string} */ source, /** @type {any} */ me, /** @type {number} */ max) {
    const ids = h.viewedIds(me.id).slice(0, max);
    if (!ids.length) return [];
    const byId = await h.viewedItems(source, me, ids);
    return ids.map((id) => byId.get(id)).filter(Boolean);
  }

  /** @param {Record<string, any[]>} found @param {any} me */
  #render(found, me) {
    const h = this.#helpers;
    /** @type {string} */
    let first = '';

    for (const tab of this.#tabs()) {
      const key = tab.dataset.btTab || '';
      const items = found[key] || [];
      const panel = this.#panel(key);
      const list = panel?.querySelector('[data-bt-list]');
      if (!items.length || !panel || !list) {
        tab.hidden = true;
        if (panel) panel.hidden = true;
        continue;
      }

      list.replaceChildren(...items.map(h.buildCard));

      const count = tab.querySelector('[data-bt-count]');
      if (count) count.textContent = String(items.length);

      const sub = panel.querySelector('[data-bt-sub]');
      const text = key === 'set' ? me.vendor : key === 'character' ? me.base || me.name : '';
      if (sub instanceof HTMLElement && text) {
        sub.textContent = text;
        sub.hidden = false;
      }

      tab.hidden = false;
      if (!first) first = key;
    }

    if (!first) {
      const note = this.querySelector('[data-bt-note]');
      if (note instanceof HTMLElement && this.dataset.design === 'true') note.hidden = false;
      return;
    }

    this.#select(first, false);
    const wrap = this.closest('[data-bt-wrap]');
    if (wrap instanceof HTMLElement) wrap.hidden = false;
  }

  /** @param {string} key @param {boolean} focus */
  #select(key, focus) {
    if (!key) return;
    for (const tab of this.#tabs()) {
      const active = tab.dataset.btTab === key;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active) {
        // Cards light up in the colour of the selected tab.
        const colour = tab.style.getPropertyValue('--bt-color').trim();
        if (colour) this.style.setProperty('--so-accent', colour);
        if (focus) tab.focus();
      }
      const panel = this.#panel(tab.dataset.btTab || '');
      if (panel) panel.hidden = !active || tab.hidden;
    }
  }

  /** Clears the shopper's viewing history (the same keys the theme writes) and removes the tab. */
  #clearHistory() {
    for (const key of HISTORY_KEYS) {
      try {
        localStorage.removeItem(key);
      } catch (_) {
        // Storage blocked: nothing to clear.
      }
    }

    const tab = this.querySelector('[data-bt-tab="viewed"]');
    const panel = this.#panel('viewed');
    const wasActive = tab?.getAttribute('aria-selected') === 'true';
    if (tab instanceof HTMLElement) tab.hidden = true;
    if (panel) panel.hidden = true;

    const next = this.#tabs().find((t) => !t.hidden);
    if (!next) {
      const wrap = this.closest('[data-bt-wrap]');
      if (wrap instanceof HTMLElement) wrap.hidden = true;
      return;
    }
    if (wasActive) this.#select(next.dataset.btTab || '', true);
  }
}

if (!customElements.get('browse-tabs')) customElements.define('browse-tabs', BrowseTabs);